import { buildPreviewTree } from './preview-tree-utils.mjs';

const { Notice, Spinner } = wp.components;
const { useEffect, useMemo, useState } = wp.element;
const { __ } = wp.i18n;

function PreviewItems({ items, maxDepth, nofollow, level = 1 }) {
	if (!items.length || (maxDepth > 0 && level > maxDepth)) {
		return null;
	}

	return (
		<ul>
			{items.map((item) => (
				<li key={item.id}>
					{item.url ? (
						<a
							href={item.url}
							rel={nofollow ? 'nofollow' : undefined}
							onClick={(event) => event.preventDefault()}
						>
							{item.title}
						</a>
					) : (
						item.title
					)}
					<PreviewItems
						items={item.children}
						maxDepth={maxDepth}
						nofollow={nofollow}
						level={level + 1}
					/>
				</li>
			))}
		</ul>
	);
}

/**
 * Render a native React preview from bounded structured REST data.
 *
 * @param {Object} props                 Component properties.
 * @param {Object} props.attributes      Current block attributes.
 * @param {string} props.path            Preview route.
 * @param {Object} [props.requestData]   Additional request context.
 * @param {string} [props.heading]       Optional preview heading.
 * @param {number} [props.maxDepth]      Maximum rendered hierarchy depth.
 * @param {boolean} [props.nofollow]     Add nofollow in the editor markup.
 * @return {Element} Native preview state.
 */
export function StructuredListPreview({
	attributes,
	path,
	requestData = {},
	heading = '',
	maxDepth = 0,
	nofollow = false,
}) {
	const [response, setResponse] = useState({
		empty_message: '',
		items: [],
		truncated: false,
	});
	const [error, setError] = useState('');
	const [loading, setLoading] = useState(true);
	const serializedAttributes = JSON.stringify(attributes);
	const serializedRequestData = JSON.stringify(requestData);

	useEffect(() => {
		let active = true;
		setLoading(true);
		setError('');

		wp.apiFetch({
			path,
			method: 'POST',
			data: {
				attributes: JSON.parse(serializedAttributes),
				...JSON.parse(serializedRequestData),
			},
		}).then(
			(nextResponse) => {
				if (active) {
					setResponse({
						empty_message: nextResponse?.empty_message || '',
						items: nextResponse?.items || [],
						truncated: Boolean(nextResponse?.truncated),
					});
					setLoading(false);
				}
			},
			() => {
				if (active) {
					setError(
						__(
							'The sitemap preview could not be loaded.',
							'simple-sitemap'
						)
					);
					setLoading(false);
				}
			}
		);

		return () => {
			active = false;
		};
	}, [path, serializedAttributes, serializedRequestData]);

	const tree = useMemo(
		() => buildPreviewTree(response.items),
		[response.items]
	);

	if (loading) {
		return (
			<div className="simple-sitemap-editor-preview" aria-busy="true">
				<Spinner />
				<span className="screen-reader-text">
					{__('Loading sitemap preview.', 'simple-sitemap')}
				</span>
			</div>
		);
	}

	if (error) {
		return (
			<Notice status="error" isDismissible={false}>
				{error}
			</Notice>
		);
	}

	return (
		<div className="simple-sitemap-editor-preview">
			{heading && <h3>{heading}</h3>}
			{tree.length ? (
				<PreviewItems
					items={tree}
					maxDepth={maxDepth}
					nofollow={nofollow}
				/>
			) : (
				<p className="no-posts">{response.empty_message}</p>
			)}
			{response.truncated && (
				<Notice status="info" isDismissible={false}>
					{__(
						'The editor preview shows the first 100 items. The published sitemap uses the block settings.',
						'simple-sitemap'
					)}
				</Notice>
			)}
		</div>
	);
}
